import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { finalize, takeUntil, timeout } from 'rxjs/operators';
import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { LeadApiService } from '../services/lead-api.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import {
  Lead,
  LeadRequest,
  LeadState,
  LeadCycle,
  LeadDisqualificationReason,
} from '../models/lead.model';
import { LeadDetailModalComponent } from '../lead-detail-modal/lead-detail-modal.component';
import {
  LeadCloseModalComponent,
  CloseLeadData,
} from '../lead-close-modal/lead-close-modal.component';
import { DealDrawerService } from '../../deals/services/deal-drawer.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { DisableWithoutPermissionDirective } from '../../../shared/directives/disable-without-permission.directive';
import { countryFromPhone, COUNTRIES_DATA } from '../../../shared/data/countries.data';
import { CountrySelectComponent } from '../../../shared/components/form/country-select/country-select.component';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../shared/utils/pagination.constants';

type ViewMode = 'leadState' | 'leadCycle';

interface LeadDisplay extends Lead {
  _stateName: string;
  _cycleName: string;
}

interface KanbanColumn {
  id: number;
  name: string;
  color: string;
  leads: LeadDisplay[];
}

interface FilterState {
  searchName: string;
  idLeadState?: number;
  idLeadCycle?: number;
  isDisqualified?: boolean;
  phonePrefix?: string;
}

@Component({
  selector: 'app-lead-kanban',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DragDropModule,
    BadgeComponent,
    LeadDetailModalComponent,
    LeadCloseModalComponent,
    DisableWithoutPermissionDirective,
    CountrySelectComponent,
  ],
  templateUrl: './lead-kanban.component.html',
  styleUrl: './lead-kanban.component.css',
})
export class LeadKanbanComponent implements OnInit, OnDestroy {
  viewMode: ViewMode = 'leadCycle';
  columns: KanbanColumn[] = [];
  loading = false;
  error = '';

  // Catálogos
  leadStates: LeadState[] = [];
  leadCycles: LeadCycle[] = [];
  disqualificationReasons: LeadDisqualificationReason[] = [];

  // Detail modal
  showDetailModal = false;
  selectedLead: Lead | null = null;

  // Close Lead modal (CLOSE_LEAD trigger)
  showCloseLeadModal = false;
  closeLeadLead: LeadDisplay | null = null;
  closeLeadTargetColumn: KanbanColumn | null = null;
  savingCloseLead = false;

  // Create Deal drawer (CREATE_DEAL trigger)
  createDealLead: LeadDisplay | null = null;
  createDealTargetColumn: KanbanColumn | null = null;
  private dealSavedInProgress = false;
  private readonly destroy$ = new Subject<void>();

  // Filtro de responsable
  private readonly OWNER_FILTER_KEY = 'crm_owner_filter_leads';
  filtroOwner: 'all' | 'mine' =
    localStorage.getItem(this.OWNER_FILTER_KEY) === 'mine' ? 'mine' : 'all';

  // Filtros
  filters: FilterState = {
    searchName: '',
    idLeadState: undefined,
    idLeadCycle: undefined,
    isDisqualified: undefined,
  };
  showFilters = false;

  // Paginación global
  globalPage = 0;
  globalTotalPages = 0;
  globalTotalElements = 0;

  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  private readonly PAGE_SIZE_KEY = 'lead-kanban-page-size';
  pageSize: number = Number(localStorage.getItem(this.PAGE_SIZE_KEY)) || 10;

  constructor(
    private leadApi: LeadApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private dealDrawerService: DealDrawerService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    this.loadCatalogs();

    // Al llegar con ?openLead=<id> (p. ej. desde el detalle de un ticket creado
    // desde ese lead), abrir el modal de detalle del lead sobre el kanban. El
    // detalle de un lead es un modal, no una ruta propia, así que este es el
    // único punto de entrada por URL — mismo patrón que ?openTicket= en tickets.
    this.route.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const raw = params.get('openLead');
        const id = raw ? Number(raw) : NaN;
        if (!Number.isNaN(id) && id > 0) this.abrirDetallePorId(id);
      });
    // When deal is saved from the CREATE_DEAL trigger drawer, change the lead's cycle
    this.dealDrawerService.dealSaved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (
          this.createDealLead?.id &&
          this.createDealTargetColumn &&
          !this.dealSavedInProgress
        ) {
          this.dealSavedInProgress = true;
          this.leadApi
            .changeCycle(this.createDealLead.id, this.createDealTargetColumn.id)
            .subscribe({
              next: () => {
                this.createDealLead = null;
                this.createDealTargetColumn = null;
                this.dealSavedInProgress = false;
                this.loadPage(this.globalPage);
              },
              error: (err) => {
                console.error(
                  'Error al cambiar ciclo después de crear deal:',
                  err,
                );
                this.confirmDialog.alert({
                  title: 'Error',
                  message:
                    'El negocio se creó correctamente, pero hubo un error al cambiar el ciclo del lead.',
                  variant: 'danger',
                });
                this.createDealLead = null;
                this.createDealTargetColumn = null;
                this.dealSavedInProgress = false;
                this.loadPage(this.globalPage);
              },
            });
        }
      });

    // If drawer is closed without saving (cancelled), revert the kanban.
    // INC 38: also guard against running while dealSaved$ is mid-flight — the
    // save path already reloads, and racing both subscriptions caused the lead
    // to briefly appear in both the source and target columns.
    this.dealDrawerService.state
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        if (!state.isOpen && this.createDealLead && !this.dealSavedInProgress) {
          setTimeout(() => {
            if (this.createDealLead && !this.dealSavedInProgress) {
              // Was not saved — revert
              this.createDealLead = null;
              this.createDealTargetColumn = null;
              this.loadPage(this.globalPage);
            }
          }, 100);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadCatalogs(): void {
    this.loading = true;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    Promise.all([
      this.leadApi.getLeadStates(businessId, true).toPromise(),
      this.leadApi.getLeadCycles(businessId, true).toPromise(),
      this.leadApi.getDisqualificationReasons(businessId, true).toPromise(),
    ])
      .then(([leadStates, leadCycles, disqualificationReasons]) => {
        this.leadStates = leadStates || [];
        this.leadCycles = leadCycles || [];
        this.disqualificationReasons = disqualificationReasons || [];
        this.initializeColumns();
        this.loading = false;
      })
      .catch((err) => {
        this.error = 'Error al cargar catálogos';
        this.loading = false;
        console.error('Error:', err);
      });
  }

  private buildColumns(): void {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-orange-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-yellow-500',
      'bg-red-500',
    ];

    switch (this.viewMode) {
      case 'leadState':
        this.columns = [
          ...this.leadStates.map((s, i) => ({
            id: s.id,
            name: s.name,
            color: colors[i % colors.length],
            leads: [],
          })),
          { id: 0, name: 'Sin Tipo', color: 'bg-gray-500', leads: [] },
        ];
        break;
      case 'leadCycle':
        this.columns = [
          ...this.leadCycles.map((c, i) => ({
            id: c.id,
            name: c.name,
            color: colors[i % colors.length],
            leads: [],
          })),
          { id: 0, name: 'Sin Ciclo', color: 'bg-gray-500', leads: [] },
        ];
        break;
    }
  }

  private initializeColumns(): void {
    this.buildColumns();
    this.loadPage(0);
  }

  /**
   * Inc 3/6: tras descalificar/reabrir el backend puede SEMBRAR un ciclo
   * "Descalificado" o un tipo (lead_state) "Reintentando" que aún no existen en
   * los catálogos cargados → esas columnas no aparecerían y la tarjeta quedaría
   * "perdida". Recargamos catálogos, reconstruimos columnas y recargamos la
   * página actual para que la columna destino exista siempre.
   */
  private reloadCatalogsAndRefresh(): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    Promise.all([
      this.leadApi.getLeadStates(businessId, true).toPromise(),
      this.leadApi.getLeadCycles(businessId, true).toPromise(),
      this.leadApi.getDisqualificationReasons(businessId, true).toPromise(),
    ])
      .then(([leadStates, leadCycles, disqualificationReasons]) => {
        this.leadStates = leadStates || [];
        this.leadCycles = leadCycles || [];
        this.disqualificationReasons = disqualificationReasons || [];
        this.buildColumns();
        this.loadPage(this.globalPage);
      })
      .catch(() => {
        // Fallback: al menos recargar los leads con las columnas actuales.
        this.loadPage(this.globalPage);
      });
  }

  private loadPage(page: number): void {
    this.loading = true;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const params: any = { businessId, page, size: this.pageSize };

    if (this.filters.searchName) params.searchName = this.filters.searchName;
    if (this.filters.phonePrefix) params.phonePrefix = this.filters.phonePrefix;
    if (this.filters.isDisqualified !== undefined)
      params.isDisqualified = this.filters.isDisqualified;
    if (this.filtroOwner === 'mine') params.userPropietyId = user?.id;

    if (this.viewMode !== 'leadState' && this.filters.idLeadState !== undefined)
      params.idLeadState = this.filters.idLeadState;
    if (this.viewMode !== 'leadCycle' && this.filters.idLeadCycle !== undefined)
      params.idLeadCycle = this.filters.idLeadCycle;

    this.columns.forEach((col) => (col.leads = []));

    // INC 21: hard timeout so the spinner never gets stuck if the request
    // hangs (network issue, backend not responding). finalize() guarantees
    // loading is cleared whatever happens.
    this.leadApi
      .searchLeads(params)
      .pipe(
        timeout(30_000),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: (response) => {
          response.content.forEach((lead) => {
            const col = this.findColumnForLead(lead);
            if (col) col.leads.push(this.enrichLead(lead));
          });
          this.globalPage = response.number ?? page;
          this.globalTotalPages = response.totalPages;
          this.globalTotalElements = response.totalElements;
        },
        error: (err) => {
          console.error('Error al cargar leads:', err);
          this.error = 'Error al cargar leads';
        },
      });
  }

  private findColumnForLead(lead: Lead): KanbanColumn | undefined {
    switch (this.viewMode) {
      case 'leadState':
        return this.columns.find((col) =>
          col.id === 0
            ? !lead.idLeadState || lead.idLeadState === 0
            : col.id === lead.idLeadState,
        );
      case 'leadCycle':
        return this.columns.find((col) =>
          col.id === 0
            ? !lead.idLeadCycle || lead.idLeadCycle === 0
            : col.id === lead.idLeadCycle,
        );
    }
  }

  changeViewMode(mode: ViewMode): void {
    this.viewMode = mode;
    this.initializeColumns();
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  setOwnerFilter(value: 'all' | 'mine'): void {
    this.filtroOwner = value;
    localStorage.setItem(this.OWNER_FILTER_KEY, value);
    this.applyFilters();
  }

  applyFilters(): void {
    this.globalPage = 0;
    this.globalTotalPages = 0;
    this.globalTotalElements = 0;
    this.loadPage(0);
  }

  changePageSize(size: number): void {
    this.pageSize = size;
    localStorage.setItem(this.PAGE_SIZE_KEY, String(size));
    this.applyFilters();
  }

  cambiarPagina(page: number): void {
    if (page < 0 || page >= this.globalTotalPages) return;
    this.loadPage(page);
  }

  get pagesArray(): number[] {
    return Array.from({ length: this.globalTotalPages }, (_, i) => i + 1);
  }

  get hasPrevPage(): boolean {
    return this.globalPage > 0;
  }
  get hasNextPage(): boolean {
    return this.globalPage < this.globalTotalPages - 1;
  }
  get pageDisplay(): number {
    return this.globalPage + 1;
  }

  clearFilters(): void {
    this.filtroOwner = 'all';
    this.filters = {
      searchName: '',
      idLeadState: undefined,
      idLeadCycle: undefined,
      isDisqualified: undefined,
      phonePrefix: undefined,
    };
    this.filtroPais = '';
    this.applyFilters();
  }

  /** Código ISO (minúscula) del país derivado del teléfono del contacto, para la bandera. */
  countryFlagCode(phone?: string | null): string | null {
    return countryFromPhone(phone)?.code.toLowerCase() ?? null;
  }
  /** Nombre del país derivado del teléfono (tooltip). */
  countryName(phone?: string | null): string {
    return countryFromPhone(phone)?.name ?? '';
  }

  /** Filtro por país (por prefijo telefónico del contacto). */
  readonly filterCountries = COUNTRIES_DATA;
  filtroPais = '';
  onCountryFilterChange(code: string): void {
    this.filtroPais = code;
    this.filters.phonePrefix = code
      ? COUNTRIES_DATA.find(c => c.code === code)?.dialCode || undefined
      : undefined;
    this.applyFilters();
  }

  get availableFilterFields(): { leadState: boolean; leadCycle: boolean } {
    return {
      leadState: this.viewMode !== 'leadState',
      leadCycle: this.viewMode !== 'leadCycle',
    };
  }

  drop(event: CdkDragDrop<LeadDisplay[]>, targetColumn: KanbanColumn): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    } else {
      const lead = event.previousContainer.data[event.previousIndex];
      // Los triggerAction viven en el catálogo de CICLOS: solo aplican en la
      // vista por Ciclo. En la vista por Tipo, targetColumn.id es un id de
      // lead_state y compararlo contra ids de lead_cycle abría por error el
      // modal de cierre / el drawer de negocio cuando los ids coincidían.
      const cycle =
        this.viewMode === 'leadCycle'
          ? this.leadCycles.find((c) => c.id === targetColumn.id)
          : undefined;

      if (cycle?.triggerAction === 'CLOSE_LEAD') {
        this.closeLeadLead = lead;
        this.closeLeadTargetColumn = targetColumn;
        this.showCloseLeadModal = true;
        return;
      }

      if (cycle?.triggerAction === 'CREATE_DEAL') {
        this.createDealLead = lead;
        this.createDealTargetColumn = targetColumn;
        this.dealDrawerService.open({
          leadId: lead.id,
          leadName: lead.name,
          contactId: lead.idContact,
          companyId: lead.idCompany,
          companyName: lead.companyName,
        });
        return;
      }

      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
      this.updateLeadField(
        event.container.data[event.currentIndex],
        targetColumn,
      );
    }
  }

  /**
   * Solo se llega aquí para movimientos SIN triggerAction: `drop()` ya
   * interceptó y retornó en los casos CLOSE_LEAD / CREATE_DEAL.
   */
  private updateLeadField(lead: LeadDisplay, targetColumn: KanbanColumn): void {
    if (!lead.id) return;

    const reload = () => this.loadPage(this.globalPage);

    if (this.viewMode === 'leadCycle') {
      if (targetColumn.id === 0) {
        reload();
        return;
      }

      this.leadApi.changeCycle(lead.id, targetColumn.id).subscribe({
        next: (updated) => {
          // Inc 43: si el lead estaba descalificado y el ciclo destino no es
          // terminal, el BACKEND lo reabre (isDisqualified=false /
          // condicion="Activo"). Reflejamos la respuesta en la tarjeta para que
          // desaparezcan el badge "Descalificado" y la opacidad reducida.
          this.applyServerLead(lead, updated, targetColumn);
        },
        error: (err) => {
          console.error('Error al cambiar ciclo:', err);
          this.confirmDialog.alert({
            title: 'Error',
            message:
              'Error al cambiar el ciclo. Por favor, recargue la página.',
            variant: 'danger',
          });
          reload();
        },
      });
      return;
    }

    const updatedLead: LeadRequest = {
      businessId: lead.businessId,
      name: lead.name,
      idContact: lead.idContact,
      idCompany: lead.idCompany,
      idLeadState: targetColumn.id === 0 ? undefined : targetColumn.id,
      idParentLead: lead.idParentLead,
      retryDate: lead.retryDate,
      isRetryScheduled: lead.isRetryScheduled,
    };

    this.leadApi.updateLead(lead.id, updatedLead).subscribe({
      next: () => {
        lead.idLeadState = targetColumn.id === 0 ? undefined : targetColumn.id;
        lead._stateName =
          targetColumn.id === 0
            ? 'Sin Tipo'
            : this.leadStates.find((s) => s.id === targetColumn.id)?.name ||
              targetColumn.name;
      },
      error: (err) => {
        console.error('Error al actualizar lead:', err);
        this.confirmDialog.alert({
          title: 'Error',
          message:
            'Error al actualizar el lead. Por favor, recargue la página.',
          variant: 'danger',
        });
        reload();
      },
    });
  }

  private enrichLead(lead: Lead): LeadDisplay {
    const state = this.leadStates.find((s) => s.id === lead.idLeadState);
    const cycle = this.leadCycles.find((c) => c.id === lead.idLeadCycle);
    return {
      ...lead,
      _stateName: lead.leadStateName || state?.name || 'Sin Tipo',
      _cycleName: lead.leadCycleName || cycle?.name || 'Sin ciclo',
    };
  }

  /**
   * Inc 43 — vuelca en la tarjeta el lead devuelto por el backend tras un
   * change-cycle. Se muta el objeto existente (en vez de recargar la página)
   * para conservar la posición que el drag-drop ya aplicó y no duplicar
   * recargas; el backend es la fuente de verdad de isDisqualified/condicion.
   */
  private applyServerLead(
    card: LeadDisplay,
    updated: Lead,
    targetColumn: KanbanColumn,
  ): void {
    Object.assign(card, updated);
    card._cycleName =
      updated.leadCycleName ||
      this.leadCycles.find((c) => c.id === updated.idLeadCycle)?.name ||
      targetColumn.name;
    card._stateName =
      updated.leadStateName ||
      this.leadStates.find((s) => s.id === updated.idLeadState)?.name ||
      'Sin Tipo';
  }

  getColumnIds(): string[] {
    return this.columns.map((col) => `lead-col-${col.id}`);
  }

  verDetalle(lead: LeadDisplay, event: Event): void {
    event.stopPropagation();
    this.selectedLead = lead;
    this.showDetailModal = true;
  }

  cerrarDetalle(): void {
    this.showDetailModal = false;
    this.selectedLead = null;
    // Limpiar ?openLead para poder reabrir el mismo lead (navegar dos veces a la
    // misma URL no vuelve a emitir el query param).
    if (this.route.snapshot.queryParamMap.has('openLead')) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { openLead: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  }

  /**
   * Carga el lead por id y abre su modal de detalle. Entrada de ?openLead=<id>:
   * se pide al backend en vez de buscarlo en las columnas para que funcione
   * aunque el lead no esté en la página cargada del kanban.
   */
  private abrirDetallePorId(id: number): void {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.leadApi
      .getLeadById(id, businessId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (lead) => {
          this.selectedLead = lead;
          this.showDetailModal = true;
        },
        error: () => {
          this.toast.error({
            title: 'No se pudo abrir el lead',
            message: 'El lead no existe o no tienes acceso.',
          });
        },
      });
  }

  nuevoLead(): void {
    this.router.navigate(['/leads/crear']);
  }

  vistaLista(): void {
    this.router.navigate(['/leads/lista']);
  }

  editarLead(lead: LeadDisplay, event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/leads/editar', lead.id]);
  }

  async eliminarLead(
    lead: LeadDisplay,
    column: KanbanColumn,
    event: Event,
  ): Promise<void> {
    event.stopPropagation();
    if (!lead.id) return;
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar lead',
      message: `¿Está seguro de eliminar el lead "${lead.name}"?`,
    });
    if (!ok) return;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;
    this.leadApi.deleteLead(lead.id, businessId).subscribe({
      next: () => {
        this.loadPage(this.globalPage);
        this.toast.deleted('Lead');
      },
      error: (err) => {
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'Error al eliminar el lead',
        });
      },
    });
  }

  reabrirLead(lead: LeadDisplay, column: KanbanColumn, event: Event): void {
    event.stopPropagation();
    if (lead.id == null) return;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    // Inc 3/37/38: reabrir usa SOLO POST /reopen (el backend elige el ciclo
    // "Reintentando"). Se refresca la columna desde el backend en vez de
    // insertar la tarjeta manualmente (evita duplicados).
    this.leadApi.reopenLead(lead.id, businessId).subscribe({
      next: () => {
        // Inc 3/6: recargar catálogos por si el backend sembró un ciclo/tipo nuevo.
        this.reloadCatalogsAndRefresh();
        this.toast.success({
          title: 'Lead reabierto',
          message: 'El lead ha sido reactivado',
        });
      },
      error: (err) => {
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'No se pudo reabrir el lead',
        });
      },
    });
  }

  confirmarCerrarLead(data: CloseLeadData): void {
    if (!this.closeLeadLead?.id || !this.closeLeadTargetColumn) return;

    this.savingCloseLead = true;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    // Inc 3/6/38: el BACKEND ya mueve el ciclo/estado tras disqualify. NO se
    // hace change-cycle/update extra en el cliente. Se recarga la vista desde
    // la respuesta del backend para garantizar una sola tarjeta por lead.
    this.leadApi
      .disqualifyLead(
        this.closeLeadLead.id,
        businessId,
        data.disqualificationReasonId,
        data.disqualificationNotes,
        data.retryDate,
        data.isRetryScheduled || undefined,
      )
      .subscribe({
        next: () => {
          this.savingCloseLead = false;
          this.showCloseLeadModal = false;
          this.closeLeadLead = null;
          this.closeLeadTargetColumn = null;
          // Inc 3/6: recargar catálogos por si el backend sembró el ciclo
          // "Descalificado" o el tipo "Reintentando".
          this.reloadCatalogsAndRefresh();
        },
        error: (err) => {
          this.savingCloseLead = false;
          this.confirmDialog.alert({
            title: 'Error',
            message: err.error?.message || 'Error al descalificar el lead',
            variant: 'danger',
          });
          this.loadPage(this.globalPage);
        },
      });
  }

  cancelarCerrarLead(): void {
    this.showCloseLeadModal = false;
    this.closeLeadLead = null;
    this.closeLeadTargetColumn = null;
    this.loadPage(this.globalPage);
  }

  get viewModeTitle(): string {
    switch (this.viewMode) {
      case 'leadState':
        return 'Por Tipo';
      case 'leadCycle':
        return 'Por Ciclo';
    }
  }

  // ── Prioridad (Inc 30) — tolera valores en inglés o español ──
  priorityColor(
    priority?: string | null,
  ): 'error' | 'warning' | 'success' | 'info' | 'light' {
    const p = (priority || '').toLowerCase();
    if (
      p.includes('urgent') ||
      p.includes('urgente') ||
      p.includes('critical') ||
      p.includes('crítica') ||
      p.includes('critica')
    )
      return 'error';
    if (p.includes('high') || p.includes('alta')) return 'error';
    if (p.includes('med') || p.includes('normal')) return 'warning';
    if (p.includes('low') || p.includes('baja')) return 'success';
    return 'light';
  }

  priorityLabel(priority?: string | null): string {
    const p = (priority || '').toLowerCase();
    if (p.includes('urgent') || p.includes('urgente')) return 'Urgente';
    if (
      p.includes('critical') ||
      p.includes('crítica') ||
      p.includes('critica')
    )
      return 'Crítica';
    if (p.includes('high') || p.includes('alta')) return 'Alta';
    if (p.includes('med') || p.includes('normal')) return 'Media';
    if (p.includes('low') || p.includes('baja')) return 'Baja';
    return priority || '—';
  }
}
